import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { AppShellV8 } from "../AppShellV8";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import { agentsFetch } from "../api";
import { useActiveClient, getCurrentMemberId } from "../activeClient";

/* ═══════════════════════════════════════════════════════════════════════════
   Agents Home — per-app GATEWAY (AMB-331 / GD-6 / GD-9).
   Four glance panels (Meetings · Reviews · Needs Attention · Campaigns), each
   showing the top few items with a "see all →" link; every row deep-links into
   its app. No KPI tiles. Empty panels read as "caught up," not broken.
   Reactive to the workspace client: the active slug is read via useActiveClient()
   and drives the fetch effect, so switching clients re-fetches every panel with
   no reload. Reviews are scoped to the signed-in member (getCurrentMemberId()).
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── API response shapes (mirror the sibling screens) ── */
interface ApiMeeting {
  id: string;
  title: string;
  client?: string | null;
  date?: string | null;
  duration?: number | null;
  status?: string | null;
  summary?: string;
  deliverableCount?: number;
  actionCount?: number;
}
interface ApiReview {
  id: string;
  title: string;
  campaign?: string;
  due?: string;
  reviewState?: string;
  submittedBy?: string | null;
  reviewer?: string | null;
  approvers?: string[];
}
interface ApiDecision {
  id: string;
  title: string;
  campaign?: string | null;
  due?: string | null;
  urgency?: string;
  assignee?: string | null;
  approvers?: string[];
  status?: string;
}
interface ApiDeliverable {
  id: string;
  title: string;
  client?: string | null;
  deliverableType?: string | null;
  campaign?: string | null;
  status?: string | null;
  reviewState?: string | null;
  due?: string | null;
  sourceMeetings?: string[] | null;
  sourceAction?: string | null; // the to-do this draft was written for
}
interface ApiCampaign {
  id: string;
  name: string;
  status?: string | null;
  phases?: { id: string; name: string }[];
}
interface ApiAction {
  id: string;
  task: string;
  client?: string | null;
  owner?: string | null;
  due?: string | null;
  status?: string | null;
  sourceMeeting?: string | null;
}

/* ── Derived row shapes ── */
type Urgency = "overdue" | "due-today" | "upcoming" | "no-date";

interface MeetingRow {
  id: string;
  title: string;
  client: string | null;
  date: string;
  upcoming: boolean;
  todos: number;
  drafts: number;
}
interface ReviewRow {
  id: string;
  title: string;
  client: string | null;
  urgency: Urgency;
  dueLabel: string;
}
interface TaskRow {
  id: string;
  task: string;
  client: string | null;
  href: string;        // → the linked draft if there is one, else the meeting digest
  dueLabel: string;    // raw due text as the AI wrote it ("Today", "This week", …)
  priority: number;    // 0 = most urgent (today), higher = less
  doc?: { id: string; title: string }; // the first draft/document written for this to-do
}
interface CampaignRow {
  id: string;
  name: string;
  client: string | null;
  phases: number;
  pct: number;
  pending: number;
}

/* ── Helpers ── */
const REVIEW_STATES = new Set(["in-review", "changes-requested"]);
const URGENCY_ORDER: Record<Urgency, number> = { overdue: 0, "due-today": 1, upcoming: 2, "no-date": 3 };

const prettify = (s?: string | null) =>
  String(s || "").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const fmtShort = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};
const fmtDue = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const urgencyFromDue = (due?: string | null): Urgency => {
  if (!due) return "no-date";
  const d = new Date(`${due}T00:00:00`);
  if (isNaN(d.getTime())) return "no-date";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (d.getTime() < today.getTime()) return "overdue";
  if (d.getTime() === today.getTime()) return "due-today";
  return "upcoming";
};

// Task priority from the (mostly free-text) due field the AI writes — today first,
// then overdue, tomorrow, this-week, other, none. Lower = more urgent.
const taskPriority = (due?: string | null): number => {
  const s = String(due || "").toLowerCase().trim();
  if (!s) return 5;
  const iso = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const d = new Date(`${iso}T00:00:00`);
    if (!isNaN(d.getTime())) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (d.getTime() === today.getTime()) return 0;
      if (d.getTime() < today.getTime()) return 1;
      return 3;
    }
  }
  if (/\b(today|eod|now|asap)\b/.test(s)) return 0;
  if (/\b(yesterday|overdue|past due)\b/.test(s)) return 1;
  if (/\btomorrow\b/.test(s)) return 2;
  if (/\bthis week\b|\b(mon|tue|wed|thu|fri|sat|sun)/.test(s)) return 3;
  return 4;
};

/* ── Small inline icons (match the app's stroke-icon language) ── */
const ICls = "h-[18px] w-[18px] text-gray-400";
const IconMeetings = () => (
  <svg className={ICls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-14 0m7 7v3m-4 0h8M12 3a3 3 0 00-3 3v5a3 3 0 006 0V6a3 3 0 00-3-3z" />
  </svg>
);
const IconReviews = () => (
  <svg className={ICls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const IconAttention = () => (
  <svg className={ICls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H6.911a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661z" />
  </svg>
);
const IconCampaigns = () => (
  <svg className={ICls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
  </svg>
);
const ArrowRight = ({ cls = "h-[11px] w-[11px]" }: { cls?: string }) => (
  <svg className={cls} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
  </svg>
);

/* ── Reusable bits ── */
function ClientChip({ name, color }: { name: string; color: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
      <span className={`h-1.5 w-1.5 rounded-full ${color}`} />
      {name}
    </span>
  );
}

function DuePill({ urgency, label }: { urgency: Urgency; label: string }) {
  if (urgency === "overdue")
    return <span className="shrink-0 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 ring-1 ring-red-200/60">Overdue</span>;
  if (urgency === "due-today")
    return <span className="shrink-0 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200/60">Due today</span>;
  return (
    <span className="shrink-0 rounded-full bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-600 ring-1 ring-gray-200/60">
      {label || "Upcoming"}
    </span>
  );
}

function PanelShell({
  icon,
  title,
  count,
  seeAllTo,
  seeAllLabel,
  loading,
  isEmpty,
  emptyMsg,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  count: number | null;
  seeAllTo: string;
  seeAllLabel: string;
  loading: boolean;
  isEmpty: boolean;
  emptyMsg: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-black/[0.06] bg-white shadow-sm ring-1 ring-black/[0.04]">
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
          {/* F2 (AMB-352): suppress the count until data is in — no misleading "0" mid-load. */}
          {!loading && count != null && <span className="text-[11px] font-medium text-gray-400">{count}</span>}
        </div>
        <Link to={seeAllTo} className="inline-flex items-center gap-0.5 text-[12px] font-medium text-blue-600 transition-all duration-150 hover:underline">
          {seeAllLabel}
          <ArrowRight />
        </Link>
      </div>
      <div className="divide-y divide-gray-50">
        {loading ? (
          <div className="space-y-2 px-4 py-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="h-4 w-4 shrink-0 animate-pulse rounded bg-gray-100" />
                <div className="h-3 flex-1 animate-pulse rounded bg-gray-100" />
                <div className="h-3 w-12 shrink-0 animate-pulse rounded bg-gray-100" />
              </div>
            ))}
          </div>
        ) : isEmpty ? (
          <div className="px-4 py-8 text-center text-[12px] text-gray-400">{emptyMsg}</div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

const ROW = "row flex items-center gap-3 px-4 py-2.5 transition-all duration-150 hover:bg-gray-50";
const SECTION_LABEL = "px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400";

export function Home() {
  const ac = useActiveClient();
  const slug = ac.slug;
  const memberId = getCurrentMemberId();
  const navigate = useNavigate();

  // AMB-430 — first-load workspace gate. On entitled load the app asks the operator to
  // ensure the workspace exists: an OWNER's new agency auto-provisions once and is routed to
  // onboarding; a MEMBER (and the Ambid dogfood) falls straight through to Home. Idempotent
  // server-side (manifest marker + lock), so a re-mount/double-load never double-provisions.
  useEffect(() => {
    let cancelled = false;
    agentsFetch("/api/agents/provision", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d && d.onboarding) navigate("/agents/onboarding");
      })
      .catch(() => {
        /* provisioning is best-effort on load; the screen still renders its empty state */
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const [loading, setLoading] = useState(true);
  const [meetings, setMeetings] = useState<MeetingRow[]>([]);
  const [reviews, setReviews] = useState<ReviewRow[]>([]);
  const [attention, setAttention] = useState<TaskRow[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);

  // Client name/color for the per-row chip. The active client resolves to its own
  // name + color; null = internal; anything else (agency "all" mode) is prettified gray.
  const clientMeta = (c: string | null): { name: string; color: string } => {
    if (!c) return { name: "Internal", color: "bg-gray-300" };
    if (c === slug) return { name: ac.name, color: ac.color };
    return { name: prettify(c), color: "bg-gray-400" };
  };

  // One effect, all four panels, keyed on the active slug → re-fetches on client switch.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const j = (path: string) =>
      fetch(`${DOCS_API_DOMAIN}${path}`).then((r) => (r.ok ? r.json() : Promise.reject(r.status)));

    Promise.allSettled([
      j(`/api/agents/meetings?client=${slug}`),
      j(`/api/agents/reviews?client=${slug}`),
      j(`/api/agents/deliverables?client=${slug}`),
      j(`/api/agents/campaigns?client=${slug}`),
      j(`/api/agents/actions?client=${slug}`),
    ])
      .then(([mRes, rRes, dRes, cRes, aRes]) => {
        if (cancelled) return;
        const now = Date.now();

        /* ── Meetings: recent (past) + upcoming (future) ── */
        const mItems: ApiMeeting[] = mRes.status === "fulfilled" ? mRes.value?.items || [] : [];
        const mRows: MeetingRow[] = mItems
          .map((m) => {
            const t = m.date ? new Date(m.date).getTime() : NaN;
            return { m, t };
          })
          .filter(({ t }) => !isNaN(t))
          .map(({ m, t }) => ({
            id: m.id,
            title: m.title,
            client: m.client || null,
            date: fmtShort(m.date),
            upcoming: t > now,
            todos: m.actionCount || 0,
            drafts: m.deliverableCount || 0,
            _t: t,
          }))
          .sort((a, b) => b._t - a._t)
          .map(({ _t, ...r }) => r);
        // Top 4 recent + top 3 upcoming (upcoming soonest-first).
        const recent = mRows.filter((m) => !m.upcoming).slice(0, 4);
        const upcoming = mRows.filter((m) => m.upcoming).reverse().slice(0, 3);
        setMeetings([...recent, ...upcoming]);

        /* ── Reviews: pending the signed-in member, priority/due order ── */
        const rAssets: ApiReview[] = rRes.status === "fulfilled" ? rRes.value?.items || [] : [];
        const rDecisions: ApiDecision[] = rRes.status === "fulfilled" ? rRes.value?.decisions || [] : [];
        const mine = (reviewer?: string | null, submittedBy?: string | null, approvers?: string[]) =>
          memberId != null &&
          (reviewer === memberId ||
            submittedBy === memberId ||
            (Array.isArray(approvers) && approvers.includes(memberId)));
        const assetRows: ReviewRow[] = rAssets
          .filter((r) => mine(r.reviewer || (r.approvers && r.approvers[0]), r.submittedBy, r.approvers))
          .map((r) => ({
            id: r.id,
            title: r.title,
            client: slug,
            urgency: urgencyFromDue(r.due),
            dueLabel: fmtDue(r.due),
          }));
        const decisionRows: ReviewRow[] = rDecisions
          .filter((d) => mine(d.assignee || (d.approvers && d.approvers[0]), null, d.approvers))
          .map((d) => ({
            id: d.id,
            title: d.title,
            client: slug,
            urgency: (["overdue", "due-today", "upcoming", "no-date"].includes(d.urgency || "")
              ? d.urgency
              : urgencyFromDue(d.due)) as Urgency,
            dueLabel: fmtDue(d.due),
          }));
        setReviews(
          [...assetRows, ...decisionRows]
            .sort((a, b) => URGENCY_ORDER[a.urgency] - URGENCY_ORDER[b.urgency])
            .slice(0, 5)
        );

        const dItems: ApiDeliverable[] = dRes.status === "fulfilled" ? dRes.value?.items || [] : [];

        /* ── Your To-Dos: OPEN TASKS, most-urgent first, each surfacing the draft/document
              written for it (deliverable.sourceAction === task id) so it opens in one click. ── */
        const draftByAction = new Map<string, { id: string; title: string }>();
        for (const d of dItems) {
          if (d.sourceAction && !draftByAction.has(d.sourceAction)) draftByAction.set(d.sourceAction, { id: d.id, title: d.title });
        }
        // Owner is a display name ("Kyle Graham", "Claire"); its first name lowercased
        // equals the member slug (kyle/claire/hannah/ryan). Show only the signed-in
        // user's own to-dos.
        const ownsTask = (owner?: string | null) =>
          String(owner || "").trim().split(/[\s/]+/)[0].toLowerCase() === memberId;
        // Meeting date from the sourceMeeting id ("<client>-YYYY-MM-DD") — drives recency.
        const meetingDate = (mid?: string | null) => (String(mid || "").match(/(\d{4}-\d{2}-\d{2})/) || ["", ""])[1];
        const aItems: ApiAction[] = aRes.status === "fulfilled" ? aRes.value?.items || [] : [];
        const taskRows: TaskRow[] = aItems
          .filter((a) => ownsTask(a.owner))
          .map((a) => {
            const doc = draftByAction.get(a.id);
            return {
              id: a.id,
              task: a.task,
              client: a.client || null,
              mDate: meetingDate(a.sourceMeeting),
              // C1 (AMB-345): open the to-do's "task page" — its meeting digest scrolled
              // to this to-do's row (linked drafts visible there), not a jump to the draft.
              href: a.sourceMeeting
                ? `/agents/digest?id=${encodeURIComponent(a.sourceMeeting)}&task=${encodeURIComponent(a.id)}`
                : "/agents/meetings",
              dueLabel: a.due || "",
              priority: taskPriority(a.due),
              doc,
            };
          })
          // Most-recent meeting first (today's meeting leads), then by due urgency.
          .sort((a, b) => (b.mDate || "").localeCompare(a.mDate || "") || a.priority - b.priority)
          .slice(0, 6)
          .map(({ mDate, ...r }) => r);
        setAttention(taskRows);

        /* ── Campaigns: light status glance (% built · phases · pending) ── */
        const cItems: ApiCampaign[] = cRes.status === "fulfilled" ? cRes.value?.items || [] : [];
        const cRows: CampaignRow[] = cItems.map((c) => {
          const dels = dItems.filter((d) => d.campaign === c.id);
          const total = dels.length;
          const secured = dels.filter((d) => d.status === "secured" || d.status === "deployed").length;
          const pending = dels.filter((d) => REVIEW_STATES.has(d.reviewState || "")).length;
          return {
            id: c.id,
            name: c.name,
            client: slug,
            phases: Array.isArray(c.phases) ? c.phases.length : 0,
            pct: total ? Math.round((secured / total) * 100) : 0,
            pending,
          };
        });
        setCampaigns(cRows.slice(0, 5));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [slug, memberId]);

  const recentMeetings = meetings.filter((m) => !m.upcoming);
  const upcomingMeetings = meetings.filter((m) => m.upcoming);

  return (
    <AppShellV8 activeApp="home" breadcrumb={["Home"]} reviewCount={reviews.length}>
      <div className="mx-auto max-w-6xl px-6 py-6">
        <div className="mb-5">
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">Home</h1>
          <p className="text-sm text-gray-500">What needs you right now — and the way into everything else.</p>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {/* ── Meetings ── */}
          <PanelShell
            icon={<IconMeetings />}
            title="Meetings"
            count={meetings.length}
            seeAllTo="/agents/meetings"
            seeAllLabel="All meetings"
            loading={loading}
            isEmpty={meetings.length === 0}
            emptyMsg="No meetings yet for this client."
          >
            {recentMeetings.length > 0 && <div className={SECTION_LABEL}>Recent</div>}
            {recentMeetings.map((m) => {
              const cm = clientMeta(m.client);
              return (
                <Link key={m.id} to={`/agents/digest?id=${encodeURIComponent(m.id)}`} className={ROW}>
                  <IconMeetings />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium text-gray-900">{m.title}</div>
                    <div className="mt-0.5 flex items-center gap-2">
                      <ClientChip name={cm.name} color={cm.color} />
                      <span className="text-[11px] text-gray-400">{m.date}</span>
                    </div>
                  </div>
                  <div className="shrink-0 text-[11px] text-gray-400">
                    {m.todos} to-dos · {m.drafts} drafts
                  </div>
                </Link>
              );
            })}
            {upcomingMeetings.length > 0 && <div className={SECTION_LABEL}>Upcoming</div>}
            {upcomingMeetings.map((m) => {
              const cm = clientMeta(m.client);
              return (
                <Link key={m.id} to="/agents/calendar" className={ROW}>
                  <svg className="h-[16px] w-[16px] text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium text-gray-900">{m.title}</div>
                    <div className="mt-0.5 flex items-center gap-2">
                      <ClientChip name={cm.name} color={cm.color} />
                      <span className="text-[11px] text-gray-400">{m.date}</span>
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full bg-gray-50 px-2 py-0.5 text-[11px] text-gray-500 ring-1 ring-gray-200">Upcoming</span>
                </Link>
              );
            })}
          </PanelShell>

          {/* ── Reviews ── */}
          <PanelShell
            icon={<IconReviews />}
            title="Reviews — waiting on you"
            count={reviews.length}
            seeAllTo="/agents/reviews"
            seeAllLabel="Review center"
            loading={loading}
            isEmpty={reviews.length === 0}
            emptyMsg="Nothing waiting on you to review right now."
          >
            {reviews.map((r) => {
              const cm = clientMeta(r.client);
              const dot =
                r.urgency === "overdue" ? "bg-red-500" : r.urgency === "due-today" ? "bg-amber-500" : "bg-gray-300";
              return (
                <Link key={r.id} to={`/agents/reviews?open=${encodeURIComponent(r.id)}`} className={ROW}>
                  <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium text-gray-900">{r.title}</div>
                    <div className="mt-0.5">
                      <ClientChip name={cm.name} color={cm.color} />
                    </div>
                  </div>
                  <DuePill urgency={r.urgency} label={r.dueLabel} />
                </Link>
              );
            })}
          </PanelShell>

          {/* ── Your To-Dos (open tasks + their drafted docs) ── */}
          <PanelShell
            icon={<IconAttention />}
            title="Your To-Dos"
            count={attention.length}
            seeAllTo="/agents/tasks"
            seeAllLabel="All My Tasks"
            loading={loading}
            isEmpty={attention.length === 0}
            emptyMsg="All caught up — no open to-dos."
          >
            {attention.map((a) => {
              const cm = clientMeta(a.client);
              const duePillCls =
                a.priority <= 1
                  ? "bg-amber-50 text-amber-700 ring-amber-200/60"
                  : "bg-gray-50 text-gray-600 ring-gray-200/60";
              // D5: when a draft exists, the hero widget deep-links into the My Tasks app
              // with that task's drawer already slid out; otherwise it goes to the meeting.
              const rowHref = a.doc ? `/agents/tasks?open=${encodeURIComponent(a.id)}` : a.href;
              return (
                <Link key={a.id} to={rowHref} className={ROW}>
                  <span className="shrink-0 rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">To-do</span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium text-gray-900">{a.task}</div>
                    <div className="mt-0.5 flex items-center gap-2">
                      <ClientChip name={cm.name} color={cm.color} />
                      {a.doc ? (
                        <span className="inline-flex min-w-0 items-center gap-1 text-[11px] text-blue-600">
                          <svg className="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                          </svg>
                          <span className="truncate">{a.doc.title}</span>
                        </span>
                      ) : (
                        <span className="text-[11px] text-gray-400">No draft</span>
                      )}
                    </div>
                  </div>
                  {a.dueLabel && (
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ${duePillCls}`}>{a.dueLabel}</span>
                  )}
                </Link>
              );
            })}
          </PanelShell>

          {/* ── Campaigns ── */}
          <PanelShell
            icon={<IconCampaigns />}
            title="Campaigns"
            count={campaigns.length}
            seeAllTo="/agents/campaigns"
            seeAllLabel="All campaigns"
            loading={loading}
            isEmpty={campaigns.length === 0}
            emptyMsg="No campaigns for this client yet."
          >
            {campaigns.map((c) => {
              const cm = clientMeta(c.client);
              return (
                <Link key={c.id} to={`/agents/campaigns?campaign=${encodeURIComponent(c.id)}`} className={ROW}>
                  <IconCampaigns />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium text-gray-900">{c.name}</div>
                    <div className="mt-0.5 flex items-center gap-2">
                      <ClientChip name={cm.name} color={cm.color} />
                      <span className="text-[11px] text-gray-400">
                        {c.phases} phase{c.phases === 1 ? "" : "s"}
                      </span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {c.pending > 0 && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200">
                        {c.pending} pending
                      </span>
                    )}
                    <span className={`text-[12px] font-semibold ${c.pct > 0 ? "text-emerald-600" : "text-gray-400"}`}>{c.pct}%</span>
                  </div>
                </Link>
              );
            })}
          </PanelShell>
        </div>
      </div>
    </AppShellV8>
  );
}
